import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Role = 'root_admin' | 'admin';
export type UserType = 'tenant' | 'root_admin';

// `accessToken` is kept in memory only and is never written to storage, so a user restored
// after a page reload has none until GianoWatcher completes the passkey sign-in again.
interface TenantUser {
  id: string
  tenantId: string
  credentialId: string
  accessToken?: string
  role: 'admin'
  type: 'tenant'
}

interface RootAdminUser {
  id: string
  credentialId: string
  accessToken?: string
  role: 'root_admin'
  type: 'root_admin'
}

type User = TenantUser | RootAdminUser

// JSON.stringify drops undefined values, so the token never reaches storage.
const omitAccessToken = (user: User): User => ({ ...user, accessToken: undefined })

interface AuthState {
  user: User | null;
  loginTenantUser: (credentialId: string, userId: string, tenantId: string, accessToken: string) => Promise<void>;
  loginRootAdminUser: (credentialId: string, userId: string, accessToken: string) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      loginTenantUser: async (credentialId: string, userId: string, tenantId: string, accessToken: string) => {
        set({ user: { id: userId, credentialId, tenantId, role: 'admin', type: 'tenant', accessToken } });
      },
      loginRootAdminUser: async (credentialId: string, userId: string, accessToken: string) => {
        set({ user: { id: userId, credentialId, role: 'root_admin', type: 'root_admin', accessToken } });
      },
      logout: () => {
        set({ user: null });
      },
    }),
    {
      name: 'auth-storage',
      // Never persist the bearer token: anything stored in localStorage can be read by any script
      // running on this origin and replayed elsewhere for the token's whole lifetime.
      partialize: (state) => ({
        user: state.user ? omitAccessToken(state.user) : null,
      }),
      // Version 0 stored the token; drop it from state saved by earlier builds (persist writes the
      // migrated state back to storage after hydrating).
      version: 1,
      migrate: (persistedState) => {
        const { user } = (persistedState ?? {}) as { user?: User | null }
        return { user: user ? omitAccessToken(user) : null }
      },
    }
  )
);